import React, { useState } from 'react';
import { Users, Building2, Activity, Brain, Cpu, Database, Download, FileText } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { useAuth } from '../../hooks/useAuth';
import { organizationApi } from '../../services/adminApi';

interface AdminDashboardProps {
  onNavigate: (page: string) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExportData = async () => {
    try {
      setExporting(true);
      setExportError(null);
      setExportSuccess(false);

      await organizationApi.exportInitialData();

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const adminSections = [
    {
      id: 'organizations',
      title: 'Organizations',
      description: 'Manage tenant organizations and their settings',
      icon: Building2,
      color: 'bg-blue-500',
      onClick: () => onNavigate('admin-organizations')
    },
    {
      id: 'users',
      title: 'Users',
      description: 'Manage all users and their permissions',
      icon: Users,
      color: 'bg-green-500',
      onClick: () => onNavigate('admin-users')
    },
    {
      id: 'activity',
      title: 'Activity Log',
      description: 'View system activity and audit logs',
      icon: Activity,
      color: 'bg-purple-500',
      onClick: () => onNavigate('admin-activity')
    },
    {
      id: 'ai-responses',
      title: 'AI Response History',
      description: 'View AI analysis results and response data',
      icon: Brain,
      color: 'bg-indigo-500',
      onClick: () => onNavigate('admin-ai-responses')
    },
    {
      id: 'ai-models',
      title: 'AI Models & Providers',
      description: 'Configure available AI providers and models for organizations',
      icon: Cpu,
      color: 'bg-pink-500',
      onClick: () => onNavigate('admin-ai-models')
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome back, {user?.full_name || user?.email}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card
              key={section.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={section.onClick}
            >
              <div className="flex items-start space-x-4">
                <div className={`${section.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {section.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {section.description}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">System Status</h3>
            <p className="text-blue-700 mt-1">All systems operational</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Healthy</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Database Management Section */}
      <Card>
        <div className="flex items-start space-x-4">
          <div className="bg-gray-700 p-3 rounded-lg">
            <Database className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Database Management
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Export and manage initial data (users and organizations)
            </p>

            {exportError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{exportError}</p>
              </div>
            )}

            {exportSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  Data exported successfully! Check your downloads folder.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      Export Initial Data
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Download a SQL file containing all users, organizations, and memberships.
                      This can be used to seed a fresh database with initial data.
                    </p>
                    <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
                      <li>Users and their permissions</li>
                      <li>Organizations and settings</li>
                      <li>User-organization memberships</li>
                      <li>Uses ON CONFLICT to prevent duplicates</li>
                    </ul>
                  </div>
                  <Button
                    onClick={handleExportData}
                    disabled={exporting}
                    variant="outline"
                    size="sm"
                    icon={Download}
                    className="ml-4 border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    {exporting ? 'Exporting...' : 'Export SQL'}
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-800 font-semibold mb-1">
                      How to use the exported SQL file:
                    </p>
                    <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
                      <li>Download the SQL file using the button above</li>
                      <li>Copy it to your server or local database</li>
                      <li>Run: <code className="bg-blue-100 px-1 py-0.5 rounded">docker exec -i postgres psql -U inventory_dev_user -d inventory_dev {'<'} initial_data.sql</code></li>
                      <li>The data will be imported without duplicates</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};